import axios, { AxiosHeaders, AxiosRequestConfig } from "axios";
import { getAccessToken } from "./authenticationUtils";

//////////////////////////////////////////////////////////////////
// http get
//////////////////////////////////////////////////////////////////
export const httpGet = async <T>(
    url: string, scopes?: string[], token?: string
): Promise<T> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Read"], token);
        const response = await axios.get<T>(url, authConfig);
        return response.data;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// http post
//////////////////////////////////////////////////////////////////
export const httpPost = async <T, Model>(
    url: string, model: Model, scopes?: string[], token?: string
): Promise<T> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Write"], token);
        const response = await axios.post<T>(url, model, authConfig);
        return response.data;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// http put
//////////////////////////////////////////////////////////////////
export const httpPut = async <T, Model>(
    url: string, model: Model, scopes?: string[], token?: string
): Promise<T> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Write"], token);
        const response = await axios.put<T>(url, model, authConfig)
        return response.data;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// http delete
//////////////////////////////////////////////////////////////////
export const httpDelete = async <T>(
    url: string, scopes?: string[], token?: string
): Promise<T> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Read"], token);
        const response = await axios.delete<T>(url, authConfig);
        return response.data;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// http post file
//////////////////////////////////////////////////////////////////
export const httpPostFile = async <T, Model>(
    url: string, model: Model,
    progressUpdated?: (perc: number) => void,
    scopes?: string[], token?: string
): Promise<T> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Read"], token);
        const postConfig: AxiosRequestConfig = {
            ...authConfig,
            onUploadProgress: function (progressEvent) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1))
                if (progressUpdated) { progressUpdated(percentCompleted); }
            }
        }
        const response = await axios.post(url, model, postConfig);
        return response.data;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// http get
//////////////////////////////////////////////////////////////////
export const httpGetBlobUrl = async (
    url: string, scopes?: string[], token?: string
): Promise<string> => {
    try {
        const authConfig = await getAuthConfig(scopes ?? ["User.Read"], token);
        const response = await axios.get(url, { ...authConfig, responseType: 'blob' });

        const windowUrl = globalThis.URL || globalThis.webkitURL;
        const blobUrl = windowUrl.createObjectURL(response.data);

        return blobUrl;
    } catch (error) {
        throw resolveError(error);
    }
}

//////////////////////////////////////////////////////////////////
// get credentials
//////////////////////////////////////////////////////////////////
// use in same file
// eslint-disable-next-line
const getAuthConfig = async (scopes: string[], token?: string): Promise<AxiosRequestConfig<any>> => {
    const accessToken = token ?? await getAccessToken(scopes);

    const headers = new AxiosHeaders();
    const bearer = `Bearer ${accessToken}`;

    headers.setAuthorization(bearer);

    return { headers }
}

//////////////////////////////////////////////////////////////////
// other functions
//////////////////////////////////////////////////////////////////
export function jsonToQueryString<Model>(model: Model): string {
    const esc = encodeURIComponent;
    // allow any parameter
    // eslint-disable-next-line
    return Object.keys(model as any).map(k => esc(k) + "=" + esc((model as any)[k])).join("&");
}

// allow any parameter
// eslint-disable-next-line
const resolveError = (error: any): string | number => {
    if (error?.response?.data?.error) {
        return error.response.data.error.toString();
    }
    if (error?.response?.data?.errors) {
        let errorMessage: string = "";
        let errorField: string;
        for (errorField in error.response.data.errors) {
            let i: number = 0;
            for (i === 0; i <= error.response.data.errors[errorField].length - 1; i++) {
                errorMessage += error.response.data.errors[errorField][i] + " ";
            }
        }
        return errorMessage;
    }
    if (error?.response?.data?.message) {
        return error.response.data.message.toString();
    }
    if (error?.response?.data) {
        return error.response.data.toString();
    }
    if (error?.message) {
        return error.message.toString();
    }
    if (error?.response) {
        return error.response.toString();
    }
    return error.toString();
};
